import { useCallback, useState } from 'react';
import { mensagemDoErro } from '../../api/erros.js';
import { atualizarOperador, desbloquearOperador, listarOperadores } from '../../api/operadores.js';
import type { Operador, SituacaoOperador } from '../../api/operadores.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { useConsulta } from '../../api/useConsulta.js';
import { usePode } from '../../autorizacao/usePode.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import type { TomDoAviso } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import { CampoBusca, ContadorRegistros, FiltroSelecao } from '../../componentes/Filtros.js';
import { IconeMais, IconeUsuarios } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import { formatarDataHora, formatarHora } from '../../componentes/datas.js';
import { FormularioDeOperador } from './FormularioDeOperador.js';
import { FormularioDePin } from './FormularioDePin.js';
import './AbaOperadores.css';

const TODAS = 'todas';

const OPCOES_DE_SITUACAO = [
  { valor: TODAS, rotulo: 'Situação · todas' },
  { valor: 'Ativo', rotulo: 'Ativos' },
  { valor: 'Inativo', rotulo: 'Inativos' },
  { valor: 'Bloqueado', rotulo: 'Bloqueados pelo freio' },
];

type Painel =
  | { readonly tipo: 'novo' }
  | { readonly tipo: 'editar'; readonly operador: Operador }
  | { readonly tipo: 'pin'; readonly operador: Operador };

type Aviso = { readonly tom: TomDoAviso; readonly texto: string };

/**
 * Freio de força bruta do PIN, em texto.
 *
 * Uma matrícula travada não é decisão de ninguém — é o relógio. Quem olha a
 * tabela precisa distinguir isso de uma desativação, senão desbloqueia o que
 * era para continuar desligado.
 */
function descreverBloqueio(operador: Operador): string {
  if (operador.bloqueado) {
    const ate = formatarHora(operador.bloqueadoAte, 'até o prazo do freio');
    return `Travado até ${ate} · ${String(operador.tentativasFalhas)} tentativas`;
  }

  if (operador.tentativasFalhas > 0) {
    return `${String(operador.tentativasFalhas)} tentativas falhas`;
  }

  return 'Sem tentativas falhas';
}

/** Operadores do app (UC13): matrícula, PIN e o freio de tentativas. */
export function AbaOperadores() {
  const pode = usePode();

  const [busca, definirBusca] = useState('');
  const [situacao, definirSituacao] = useState<string>(TODAS);
  const [pagina, definirPagina] = useState(1);
  const [painel, definirPainel] = useState<Painel | null>(null);
  const [aDesativar, definirADesativar] = useState<Operador | null>(null);
  const [erroDoDialogo, definirErroDoDialogo] = useState<string | null>(null);
  const [agindo, definirAgindo] = useState(false);
  const [aviso, definirAviso] = useState<Aviso | null>(null);

  const carregar = useCallback(
    () =>
      listarOperadores({
        pagina,
        ...seInformado('busca', busca),
        ...seInformado('situacao', situacao === TODAS ? null : (situacao as SituacaoOperador)),
      }),
    [pagina, busca, situacao],
  );

  const consulta = useConsulta(carregar);
  const { recarregar } = consulta;

  function trocarFiltro(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  function concluir(texto: string) {
    definirPainel(null);
    definirAviso({ tom: 'ok', texto });
    recarregar();
  }

  function alternarAtivo(operador: Operador, ativo: boolean) {
    definirAgindo(true);
    definirErroDoDialogo(null);

    atualizarOperador(operador.id, { ativo })
      .then(() => {
        definirAgindo(false);
        definirADesativar(null);
        definirAviso({
          tom: 'ok',
          texto: ativo
            ? `${operador.matricula} voltou a entrar no coletor.`
            : `${operador.matricula} não entra mais no coletor.`,
        });
        recarregar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        const texto = mensagemDoErro(falha);

        if (ativo) {
          definirAviso({ tom: 'erro', texto });
        } else {
          definirErroDoDialogo(texto);
        }
      });
  }

  function soltarFreio(operador: Operador) {
    desbloquearOperador(operador.id)
      .then(() => {
        definirAviso({ tom: 'ok', texto: `${operador.matricula} pode entrar de novo no coletor.` });
        recarregar();
      })
      .catch((falha: unknown) => {
        definirAviso({ tom: 'erro', texto: mensagemDoErro(falha) });
      });
  }

  const gerencia = pode('operadores.gerenciar');

  const colunas: readonly Coluna<Operador>[] = [
    {
      chave: 'matricula',
      rotulo: 'Matrícula',
      largura: '13%',
      celula: (operador) => <span className="operadores__matricula">{operador.matricula}</span>,
    },
    { chave: 'nome', rotulo: 'Nome', largura: '22%', celula: (operador) => operador.nome },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      largura: '13%',
      celula: (operador) =>
        operador.bloqueado ? (
          <ChipStatus tom="falha" rotulo="Bloqueado" tamanho="tabela" />
        ) : operador.ativo ? (
          <ChipStatus tom="concluida" rotulo="Ativo" tamanho="tabela" />
        ) : (
          <ChipStatus tom="desativado" rotulo="Inativo" tamanho="tabela" />
        ),
    },
    {
      chave: 'acesso',
      rotulo: 'Último acesso',
      largura: '15%',
      celula: (operador) => (
        <span className="operadores__discreto">
          {formatarDataHora(operador.ultimoAcessoEm, 'Nunca acessou')}
        </span>
      ),
    },
    {
      chave: 'bloqueio',
      rotulo: 'Freio de tentativas',
      largura: '20%',
      celula: (operador) => (
        <span
          className={
            operador.bloqueado
              ? 'operadores__freio operadores__freio--travado'
              : 'operadores__freio'
          }
        >
          {descreverBloqueio(operador)}
        </span>
      ),
    },
    {
      chave: 'acoes',
      rotulo: '',
      alinhamento: 'direita',
      celula: (operador) => {
        if (!gerencia) {
          return null;
        }

        return (
          <div className="operadores__acoes">
            <Botao
              altura="pequena"
              aria-label={`Editar ${operador.matricula}`}
              onClick={() => {
                definirPainel({ tipo: 'editar', operador });
              }}
            >
              Editar
            </Botao>
            <Botao
              altura="pequena"
              aria-label={`Redefinir PIN de ${operador.matricula}`}
              onClick={() => {
                definirPainel({ tipo: 'pin', operador });
              }}
            >
              PIN
            </Botao>
            {operador.bloqueado && (
              <Botao
                altura="pequena"
                variante="atencao"
                aria-label={`Desbloquear ${operador.matricula}`}
                onClick={() => {
                  soltarFreio(operador);
                }}
              >
                Desbloquear
              </Botao>
            )}
            {operador.ativo ? (
              <Botao
                altura="pequena"
                variante="destrutivo"
                aria-label={`Desativar ${operador.matricula}`}
                onClick={() => {
                  definirErroDoDialogo(null);
                  definirADesativar(operador);
                }}
              >
                Desativar
              </Botao>
            ) : (
              <Botao
                altura="pequena"
                variante="sucesso"
                aria-label={`Ativar ${operador.matricula}`}
                onClick={() => {
                  alternarAtivo(operador, true);
                }}
              >
                Ativar
              </Botao>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="operadores">
      <FaixaDeFiltros rotulo="Filtros de operadores do app">
        <CampoBusca
          rotulo="Buscar por matrícula ou nome"
          valor={busca}
          largura={260}
          aoMudar={(valor) => {
            trocarFiltro(() => {
              definirBusca(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Filtrar por situação do operador"
          valor={situacao}
          opcoes={OPCOES_DE_SITUACAO}
          aoMudar={(valor) => {
            trocarFiltro(() => {
              definirSituacao(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>
            {contagemDoRecorte(consulta.dados.total, 'operador', 'operadores')}
          </ContadorRegistros>
        )}
        {gerencia && (
          <span className="operadores__acao-principal">
            <Botao
              variante="primario"
              altura="media"
              onClick={() => {
                definirPainel({ tipo: 'novo' });
              }}
            >
              <IconeMais tamanho={15} />
              Novo operador
            </Botao>
          </span>
        )}
      </FaixaDeFiltros>

      {aviso !== null && <AvisoDaTela tom={aviso.tom}>{aviso.texto}</AvisoDaTela>}

      <Resultado
        consulta={consulta}
        espera="Carregando operadores do app…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeUsuarios tamanho={22} />}
            titulo="Nenhum operador do app neste recorte"
            detalhe="Ajuste a busca ou a situação para encontrar quem procura."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={colunas}
            chaveDe={(operador) => operador.id}
            descricao="Operadores do app"
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'operador', 'operadores')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>

      {painel?.tipo === 'novo' && (
        <FormularioDeOperador
          operador={null}
          aoConcluir={concluir}
          aoFechar={() => {
            definirPainel(null);
          }}
        />
      )}

      {painel?.tipo === 'editar' && (
        <FormularioDeOperador
          operador={painel.operador}
          aoConcluir={concluir}
          aoFechar={() => {
            definirPainel(null);
          }}
        />
      )}

      {painel?.tipo === 'pin' && (
        <FormularioDePin
          operador={painel.operador}
          aoConcluir={concluir}
          aoFechar={() => {
            definirPainel(null);
          }}
        />
      )}

      {aDesativar !== null && (
        <DialogoConfirmacao
          titulo={`Desativar o operador ${aDesativar.matricula}?`}
          rotuloConfirmar="Desativar operador"
          agindo={agindo}
          aoConfirmar={() => {
            alternarAtivo(aDesativar, false);
          }}
          aoCancelar={() => {
            definirADesativar(null);
            definirErroDoDialogo(null);
          }}
        >
          <p>
            {aDesativar.nome} deixa de entrar no coletor, e a sessão aberta dele no coletor é
            encerrada na hora.
          </p>
          <p>
            O cadastro não é apagado: as operações que ele já registrou continuam apontando para
            esta matrícula.
          </p>
          {erroDoDialogo !== null && <p className="operadores__erro">{erroDoDialogo}</p>}
        </DialogoConfirmacao>
      )}
    </div>
  );
}
